import React, { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../../src/lib/supabase';
import { useAuth } from '../../../src/contexts/AuthContext';
import { logUsageEvent } from '../../../src/lib/logger';
import { Bell, Check, X, Loader2, Undo2 } from 'lucide-react';
import { useToast } from '../../../src/contexts/ToastContext';
import { normalizeUcCodigo, ucCodigosDaFatura } from '../../../src/lib/ucCodigo';
import { avisoValeNoMes, type AvisoUc } from '../../../src/lib/avisosUc';
import { buscarAvisosDasUcs, buscarCiencias, desmarcarCiente, encerrarAviso, marcarCiente, type CienciaAviso } from '../../../src/services/avisosUc';
import AvisoCard from './avisos/AvisoCard';
import EncerrarAvisoModal from './avisos/EncerrarAvisoModal';

// Avisos da UC que valem na fatura aberta — topo da validação, do cálculo e da emissão.
// Desde 04/11/2026 um aviso tem período (ver src/lib/avisosUc.ts): "Ciente neste mês"
// registra que foi aplicado nesta fatura e ele continua nas próximas; "Encerrar" tira
// de todas. A lista completa da UC fica na aba "Avisos" do modal da UC.
interface LembretesViewerProps {
    uc_id?: string | number;
    uc_codigo?: string;
    /** Mês da fatura ("OUT/2026"). Sem ele, mostra todos os avisos pendentes. */
    mes_referencia?: string;
    /** Fatura aberta: liga o "Ciente neste mês". */
    fatura_id?: string | number | null;
    onUpdate?: () => void;
}

/** UCs da fatura pelo número normalizado: a fatura vem só com dígitos e a UC pode estar formatada (#6599). */
async function idsDasUcs(ucId: string | number | undefined, ucCodigo: string | undefined): Promise<string[]> {
    if (ucId) return [String(ucId)];
    if (!ucCodigo) return [];
    const chaves = Array.from(new Set([normalizeUcCodigo(ucCodigo), ...ucCodigosDaFatura(ucCodigo)]))
        .map(c => c.replace(/["\\]/g, ''))
        .filter(Boolean);
    if (chaves.length === 0) return [];
    const lista = chaves.map(c => `"${c}"`).join(',');
    const { data } = await supabase
        .from('unidades_consumidoras')
        .select('id')
        .or(`numero_uc_norm.in.(${lista}),outro_numero_uc_norm.in.(${lista})`);
    return (data || []).map(u => String(u.id));
}

const LembretesViewer: React.FC<LembretesViewerProps> = ({ uc_id, uc_codigo, mes_referencia, fatura_id, onUpdate }) => {
    const { user, profile } = useAuth();
    const { addToast } = useToast();
    const [avisos, setAvisos] = useState<AvisoUc[]>([]);
    const [ciencias, setCiencias] = useState<Record<string, CienciaAviso>>({});
    const [loading, setLoading] = useState(true);
    const [acaoEm, setAcaoEm] = useState<string | null>(null);
    const [encerrando, setEncerrando] = useState<AvisoUc | null>(null);
    // Fila do cálculo troca de fatura com a leitura em voo: só a última resposta vale.
    const pedido = useRef(0);

    const autor = { id: user?.id ?? null, nome: profile?.full_name || user?.user_metadata?.full_name || user?.email || 'Usuário' };

    const carregar = useCallback(async () => {
        const meu = ++pedido.current;
        setLoading(true);
        try {
            const ids = await idsDasUcs(uc_id, uc_codigo);
            const todos = await buscarAvisosDasUcs(supabase, ids, { soPendentes: true });
            const valem = mes_referencia ? todos.filter(a => avisoValeNoMes(a, mes_referencia)) : todos;
            const desta = fatura_id != null ? await buscarCiencias(supabase, valem.map(a => a.id), fatura_id) : [];
            if (meu !== pedido.current) return;
            setAvisos(valem);
            setCiencias(Object.fromEntries(desta.map(c => [c.lembrete_id, c])));
        } catch (err) {
            if (meu !== pedido.current) return;
            console.error('Erro ao buscar avisos da UC:', err);
            setAvisos([]);
        } finally {
            if (meu === pedido.current) setLoading(false);
        }
    }, [uc_id, uc_codigo, mes_referencia, fatura_id]);

    useEffect(() => {
        if (uc_id || uc_codigo) carregar();
        else { setAvisos([]); setLoading(false); }
    }, [carregar, uc_id, uc_codigo]);

    const handleCiente = async (aviso: AvisoUc) => {
        if (fatura_id == null || !mes_referencia) return;
        setAcaoEm(aviso.id);
        try {
            await marcarCiente(supabase, aviso, fatura_id, mes_referencia, autor);
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'lembretes_cobranca', acao: 'ciente_aviso', origem: 'acao' });
            await carregar();
            onUpdate?.();
        } catch (err) {
            console.error(err);
            addToast('Não foi possível registrar o ciente. O banco pode estar sem a atualização dos avisos: avise a Tecnologia.', 'error');
        } finally {
            setAcaoEm(null);
        }
    };

    const handleDesfazer = async (ciencia: CienciaAviso) => {
        setAcaoEm(ciencia.lembrete_id);
        try {
            await desmarcarCiente(supabase, ciencia.id);
            await carregar();
            onUpdate?.();
        } catch (err) {
            console.error(err);
            addToast('Não foi possível desfazer o ciente.', 'error');
        } finally {
            setAcaoEm(null);
        }
    };

    const handleEncerrar = async (motivo: string) => {
        if (!encerrando) return;
        setAcaoEm(encerrando.id);
        try {
            await encerrarAviso(supabase, encerrando.id, motivo, autor);
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'lembretes_cobranca', acao: 'encerrar_aviso', origem: 'acao' });
            addToast('Aviso encerrado.', 'success');
            setEncerrando(null);
            await carregar();
            onUpdate?.();
        } catch (err) {
            console.error(err);
            addToast('Erro ao encerrar o aviso.', 'error');
        } finally {
            setAcaoEm(null);
        }
    };

    if (loading) {
        return <div className="flex items-center justify-center p-4"><Loader2 className="animate-spin text-primary" size={24} /></div>;
    }

    if (avisos.length === 0) return null;

    return (
        <div className="mb-6 space-y-3">
            <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400 font-bold px-1">
                <Bell size={20} />
                <h2>Avisos e Lembretes Pendentes ({avisos.length})</h2>
            </div>

            <div className="grid grid-cols-1 gap-3">
                {avisos.map(aviso => {
                    const ciencia = ciencias[aviso.id] ?? null;
                    const ocupado = acaoEm === aviso.id;
                    return (
                        <AvisoCard
                            key={aviso.id}
                            aviso={aviso}
                            mesFatura={mes_referencia}
                            ciencia={ciencia}
                            cienciaEhDestaFatura
                            acoes={(
                                <>
                                    {fatura_id != null && mes_referencia && (ciencia ? (
                                        <button
                                            type="button"
                                            onClick={() => handleDesfazer(ciencia)}
                                            disabled={ocupado}
                                            title="Desfazer o ciente desta fatura"
                                            className="px-3 py-1.5 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold text-sm rounded-lg flex items-center gap-1 transition-colors disabled:opacity-50"
                                        >
                                            <Undo2 size={15} /> Desfazer
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => handleCiente(aviso)}
                                            disabled={ocupado}
                                            title="Registra que o aviso foi aplicado nesta fatura. Ele continua nas próximas."
                                            className="px-3 py-1.5 bg-green-100 hover:bg-green-200 dark:bg-green-900/30 dark:hover:bg-green-900/50 text-green-700 dark:text-green-400 font-semibold text-sm rounded-lg flex items-center gap-1 transition-colors disabled:opacity-50"
                                        >
                                            {ocupado ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Ciente neste mês
                                        </button>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={() => setEncerrando(aviso)}
                                        disabled={ocupado}
                                        title="Tira o aviso de todas as próximas faturas"
                                        className="px-3 py-1.5 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 font-semibold text-sm rounded-lg flex items-center gap-1 transition-colors disabled:opacity-50"
                                    >
                                        <X size={15} /> Encerrar
                                    </button>
                                </>
                            )}
                        />
                    );
                })}
            </div>

            <EncerrarAvisoModal
                aviso={encerrando}
                salvando={!!encerrando && acaoEm === encerrando.id}
                onCancelar={() => setEncerrando(null)}
                onConfirmar={handleEncerrar}
            />
        </div>
    );
};

export default LembretesViewer;
